import {
  ENV,
  authFetch,
  parseResponse,
  SUCCESSARRAY,
} from "../utils";

function normalizeString(value) {
  return String(value || "").trim();
}

function normalizeObjectId(value) {
  if (!value || value === "null") {
    return null;
  }

  if (typeof value === "object") {
    return value._id || value.id || null;
  }

  return value;
}

function createProductId(value) {
  return normalizeString(value)
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/Ñ/g, "N")
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function createSlug(value) {
  return normalizeString(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ñ/g, "n")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function createSku(value) {
  return normalizeString(value)
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/Ñ/g, "N")
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

function normalizeNumber(value, defaultValue = null) {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }

  const numberValue = Number(value);

  return Number.isFinite(numberValue)
    ? numberValue
    : defaultValue;
}

function normalizeInteger(value, defaultValue = 0) {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }

  const numberValue = Number(value);

  return Number.isInteger(numberValue)
    ? numberValue
    : defaultValue;
}

function normalizeStringArray(value) {
  if (!value) {
    return [];
  }

  const values = Array.isArray(value) ? value : [value];

  return [
    ...new Set(
      values
        .map((item) => normalizeString(item))
        .filter(Boolean)
    ),
  ];
}

function normalizeObjectIdArray(value) {
  if (!value) {
    return [];
  }

  const values = Array.isArray(value) ? value : [value];

  return [
    ...new Set(
      values
        .map((item) => normalizeObjectId(item))
        .filter(Boolean)
    ),
  ];
}

function normalizeVariantAttributes(attributes = []) {
  if (!Array.isArray(attributes)) {
    return [];
  }

  return attributes
    .map((attribute) => ({
      schema: normalizeObjectId(attribute.schema),
      classification: normalizeObjectId(
        attribute.classification
      ),
      name: normalizeString(attribute.name),
      value: normalizeString(attribute.value),
      label: normalizeString(attribute.label),
    }))
    .filter((attribute) => attribute.value);
}

function normalizeVariants(variants = []) {
  if (!Array.isArray(variants)) {
    return [];
  }

  return variants.map((variant) => ({
    sku: createSku(variant.sku),

    attributes: normalizeVariantAttributes(variant.attributes),

    price: normalizeNumber(variant.price, 0),

    offerPrice: normalizeNumber(variant.offerPrice, null),

    cost: normalizeNumber(variant.cost, null),

    stock: normalizeInteger(variant.stock, 0),

    lowStockAlert: normalizeInteger(
      variant.lowStockAlert,
      0
    ),

    enabled: variant.enabled !== false,

    images: normalizeStringArray(variant.images),
  }));
}

function normalizeImages(images = []) {
  if (!Array.isArray(images)) {
    return [];
  }

  return images.map((image, index) => ({
    _id: image?._id || null,
    localId: normalizeString(image?.localId),
    path: image?.file ? "" : normalizeString(image?.path),
    name: normalizeString(image?.name || image?.file?.name),
    order: Number(image?.order) || index + 1,
    isCover: Boolean(image?.isCover),
  }));
}

function createProductData(data) {
  return {
    id: createProductId(data.id || data.name),

    name: normalizeString(data.name),

    slug: createSlug(data.slug || data.name),

    category: normalizeObjectId(data.category),

    brand: normalizeObjectId(data.brand),

    description: normalizeString(data.description),

    characteristics: normalizeObjectIdArray(data.characteristics),

    variants: normalizeVariants(data.variants),

    images: normalizeImages(data.images),

    visible: data.visible ?? true,

    featured: data.featured ?? false,

    seo: {
      metaTitle: normalizeString(data.seo?.metaTitle),
      metaDescription: normalizeString(data.seo?.metaDescription),
    },
  };
}

function createProductFormData(data) {
  const productData = createProductData(data);
  const formData = new FormData();

  formData.append("data", JSON.stringify(productData));

  if (Array.isArray(data.images)) {
    data.images.forEach((image) => {
      if (image instanceof File) {
        formData.append("images", image);
        return;
      }

      if (image?.file instanceof File) {
        formData.append("images", image.file);
      }
    });
  }

  return formData;
}

export class Product {
  async createProduct(data) {
    const url = `${ENV.API_URL}/${ENV.ENDPOINTS.PRODUCT.PRODUCT}`;

    const params = {
      method: "POST",
      body: createProductFormData(data),
    };

    const response = await authFetch(url, params);
    const result = await parseResponse(response);

    if (!SUCCESSARRAY.includes(response.status)) {
      throw result;
    }

    return result;
  }

  async getProducts(page = 1, limit = 10, filters = {}) {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          value !== ""
      )
    );

    const queryParams = new URLSearchParams({
      page,
      limit,
      ...cleanFilters,
    });

    const url =
      `${ENV.API_URL}/${ENV.ENDPOINTS.PRODUCT.PRODUCTS}` +
      `?${queryParams.toString()}`;

    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!SUCCESSARRAY.includes(response.status)) {
      throw result;
    }

    return result;
  }

  async getNextSku(categoryId, brandId) {
    const queryParams = new URLSearchParams(
      Object.fromEntries(
        Object.entries({ category: categoryId, brand: brandId }).filter(
          ([, value]) => value
        )
      )
    );

    const url =
      `${ENV.API_URL}/${ENV.ENDPOINTS.PRODUCT.NEXT_SKU}` +
      `?${queryParams.toString()}`;

    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!SUCCESSARRAY.includes(response.status)) {
      throw result;
    }

    return result;
  }

  async getProduct(productId) {
    const url =
      `${ENV.API_URL}/${ENV.ENDPOINTS.PRODUCT.PRODUCT}` +
      `/${productId}`;

    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!SUCCESSARRAY.includes(response.status)) {
      throw result;
    }

    return result;
  }

  async updateProduct(productId, data) {
    const url =
      `${ENV.API_URL}/${ENV.ENDPOINTS.PRODUCT.PRODUCT}` +
      `/${productId}`;

    const params = {
      method: "PATCH",
      body: createProductFormData(data),
    };

    const response = await authFetch(url, params);
    const result = await parseResponse(response);

    if (!SUCCESSARRAY.includes(response.status)) {
      throw result;
    }

    return result;
  }

  async deleteProduct(productId) {
    const url =
      `${ENV.API_URL}/${ENV.ENDPOINTS.PRODUCT.PRODUCT}` +
      `/${productId}`;

    const params = {
      method: "DELETE",
    };

    const response = await authFetch(url, params);
    const result = await parseResponse(response);

    if (!SUCCESSARRAY.includes(response.status)) {
      throw result;
    }

    return result;
  }
}